<script setup lang="ts">
import { ANCHOR_OFFSET_PX, VIEWPORT_MARGIN_PX } from '@/composables/useCalendarEventPreview';
import type { TimelineBlock } from '@/composables/useSlotDialog';
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

/**
 * Per-block tooltip for the resource timeline bar (D-15).
 *
 * Bespoke rather than PrimeVue's v-tooltip directive: the directive anchors to the element it is bound to,
 * and the only element with pointer events on this timeline is the whole bar (every block is
 * pointer-events-none so drag-to-select passes through). The directive would therefore centre on the entire
 * timeline rather than the hovered block, and it does not reliably reposition when only its content changes.
 * This keeps the PrimeVue tooltip *look* — supplied entirely by plan 02-02's .timeline-block-tooltip* classes —
 * while anchoring per-block.
 *
 * Presentational only: no emits and no store. It does use i18n for exactly one string — the D-09 reduced-capacity
 * line, whose "max {capacity}" wrapper is copy rather than block data. Everything else it renders is block data.
 */
interface Props {
    block: TimelineBlock | null;
    anchorRect: DOMRect | null;
}

const props = defineProps<Props>();

const { t } = useI18n();

const tooltipRef = ref<HTMLElement | null>(null);
const top = ref(0);
const left = ref(0);

const isVisible = computed(() => props.block !== null && props.anchorRect !== null);

// D-06: an absent description renders no line at all, leaving title + range — identical to a reservation block.
const descriptionText = computed(() => {
    const value = props.block?.description;
    if (typeof value !== 'string') {
        return '';
    }
    return value.trim();
});

// D-09: the accessible path for the cap. On a block narrower than the 15% label threshold the in-bar label is hidden
// entirely, and white on bg-gray-400 at opacity-50 is below AA anyway — so this is where the cap is reliably legible.
// D-06's omit-the-row rule: rendered only when the block actually carries a cap.
const reducedCapacityText = computed(() => {
    const capacity = props.block?.reducedCapacity;
    if (capacity == null) {
        return '';
    }
    return t('calendar.new.resourcesSlots.timeline.maxCapacity', { capacity });
});

// startTime / endTime are already HH:mm strings on the block. Not reformatted, no Date constructed.
const rangeText = computed(() => {
    if (props.block === null) {
        return '';
    }
    return `${props.block.startTime} – ${props.block.endTime}`;
});

/**
 * Centres the tooltip on the anchor, sits above it, flips below rather than clipping the viewport top,
 * and clamps horizontally. Reads its own offsetWidth/offsetHeight, so it can only run once the tooltip
 * is in the DOM — the width depends on the content, so the centring cannot be computed before render.
 */
function updatePlacement() {
    const rect = props.anchorRect;
    const el = tooltipRef.value;
    if (rect === null || el === null) {
        return;
    }

    const tooltipWidth = el.offsetWidth;
    const tooltipHeight = el.offsetHeight;

    let nextTop = rect.top - tooltipHeight - ANCHOR_OFFSET_PX;
    if (nextTop < VIEWPORT_MARGIN_PX) {
        nextTop = rect.bottom + ANCHOR_OFFSET_PX;
    }

    let nextLeft = rect.left + rect.width / 2 - tooltipWidth / 2;
    nextLeft = Math.min(nextLeft, window.innerWidth - tooltipWidth - VIEWPORT_MARGIN_PX);
    nextLeft = Math.max(nextLeft, VIEWPORT_MARGIN_PX);

    // Both edges, like the horizontal pair above: the flip-below branch can otherwise push a multi-line tooltip
    // past the viewport bottom when the bar sits low in a tall dialog on a short screen. Math.min before
    // Math.max so a tooltip taller than the viewport pins to the top margin rather than a negative offset.
    nextTop = Math.min(nextTop, window.innerHeight - tooltipHeight - VIEWPORT_MARGIN_PX);
    nextTop = Math.max(nextTop, VIEWPORT_MARGIN_PX);

    top.value = nextTop;
    left.value = nextLeft;
}

watch(
    () => [props.block, props.anchorRect],
    async () => {
        if (!isVisible.value) {
            return;
        }
        await nextTick();
        updatePlacement();
    },
    { immediate: true }
);

const tooltipStyle = computed(() => ({
    top: `${top.value}px`,
    left: `${left.value}px`
}));
</script>

<template>
    <Teleport to="body">
        <div v-if="isVisible && block" ref="tooltipRef" class="timeline-block-tooltip" role="tooltip" :style="tooltipStyle">
            <div class="timeline-block-tooltip__title">{{ block.title }}</div>
            <div class="timeline-block-tooltip__range">{{ rangeText }}</div>
            <div v-if="reducedCapacityText" class="timeline-block-tooltip__range">{{ reducedCapacityText }}</div>
            <div v-if="descriptionText" class="timeline-block-tooltip__description">{{ descriptionText }}</div>
        </div>
    </Teleport>
</template>
